import { useAuth0 } from "@auth0/auth0-react";
import { ArchiveBoxIcon, ArchiveBoxXMarkIcon, ClockIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { addDays, differenceInDays, differenceInHours, differenceInMinutes, format } from "date-fns";
import React from "react";
import { FaCircle } from "react-icons/fa";

import { Badge, Paragraph, WYSIWYG } from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import {
  ApiChatStatusChoices,
  ApiChatTypeChoices,
  ChatType,
  useFinishChatMutation,
  useOpenChatMutation,
  useUpdateMessageReadStatusMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { useBreakpoint } from "@pages/VideoSessionsV2/hooks/useMediaMatcher";

interface ChatCardProps {
  chat: ChatType;
  isSelected: boolean;
  onArchive?: () => void;
}

export const ChatCard: React.FC<ChatCardProps> = ({ chat, isSelected, onArchive }) => {
  const { user } = useAuth0();
  const { isMobile } = useBreakpoint();

  const updateMessageReadStatusMutation = useUpdateMessageReadStatusMutation({});
  const finishChatMutation = useFinishChatMutation({});
  const openChatMutation = useOpenChatMutation({});

  // TODO: using invalidateQueries should be temporary and we should rebuild the chat UI around the WS updating the chats state directly to avoid a bunch of refetches
  const queryClient = useQueryClient();

  const learnerFirstName = chat.learner.learnermetadata?.firstName;
  const learnerLastName = chat.learner.learnermetadata?.lastName;

  const caregiverFirstName = chat.learner.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.firstName;
  const caregiverLastName = chat.learner.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.lastName;

  const isArchived = chat.status === ApiChatStatusChoices.Finished;

  const formatDate = (date: Date): string => {
    const now = new Date();
    const minutesDiff = differenceInMinutes(now, date);
    const hoursDiff = differenceInHours(now, date);
    const daysDiff = differenceInDays(now, date);

    // If less than 1 hour, show minutes
    if (minutesDiff < 60) {
      return `${minutesDiff}m`;
    }

    // If less than 1 day, show hours
    if (hoursDiff < 24) {
      return `${hoursDiff}h`;
    }

    // If less than 7 days, show days
    if (daysDiff < 7) {
      return `${daysDiff}d`;
    }

    // 7 days or older, show date format
    const isDifferentYear = date.getFullYear() !== now.getFullYear();
    if (isDifferentYear) {
      return format(date, "MMM do, yyyy"); // "Dec. 4th, 2025"
    }
    return format(date, "MMM do"); // "Dec. 4th"
  };

  const timeRemainingMinutes =
    chat.lastMessage?.createdDate && chat.lastMessage.user?.auth0id !== user?.sub
      ? differenceInMinutes(addDays(new Date(chat.lastMessage.createdDate), 1), new Date())
      : 0;

  const getTimeRemaining = (lastMessageDate: Date): string => {
    const deadline = addDays(lastMessageDate, 1);
    const now = new Date();

    const minutesDiff = differenceInMinutes(deadline, now);
    const hoursDiff = differenceInHours(deadline, now);

    if (minutesDiff < 60) {
      return `${minutesDiff}m`;
    }

    return `${hoursDiff}h`;
  };

  const handleMarkAsRead = async (read: boolean) => {
    if (!chat.lastMessage) return;

    try {
      const response = await updateMessageReadStatusMutation.mutateAsync({
        input: {
          messageId: chat.lastMessage.id,
          read: read,
        },
      });

      if (!response.updateMessageReadStatus?.success) {
        notifyError(response.updateMessageReadStatus?.message || "Failed to mark chat message as read");
        return;
      }

      // Invalidate to refetch updated data
      queryClient.invalidateQueries({ queryKey: ["getExpertChatMembers"] });
    } catch (_error) {
      notifyError("Failed to mark chat message as read");
    }
  };

  const handleArchiveChat = async () => {
    try {
      const response = await finishChatMutation.mutateAsync({
        chatId: chat.id,
      });

      if (!response.finishChat?.success) {
        notifyError(response.finishChat?.message || "Failed to archive chat");
        return;
      }

      // refetch chats list
      queryClient.invalidateQueries({ queryKey: ["getExpertChatMembers"] });
      if (isSelected) onArchive?.();
    } catch (_error) {
      notifyError("Failed to archive chat");
    }
  };

  const handleUnarchiveChat = async () => {
    try {
      const response = await openChatMutation.mutateAsync({
        chatId: chat.id,
      });

      if (!response.openChat?.success) {
        notifyError(response.openChat?.message || "Failed to unarchive chat");
        return;
      }

      // refetch chats list
      queryClient.invalidateQueries({ queryKey: ["getExpertChatMembers"] });
    } catch (_error) {
      notifyError("Failed to unarchive chat");
    }
  };

  const popoverMenuItems: PopoverMenuItem[] = [
    ...(chat.isUnread
      ? [
          {
            label: "Mark as Read",
            Icon: EyeIcon,
            callback: () => {
              handleMarkAsRead(true);
            },
          },
        ]
      : [
          {
            label: "Mark as Unread",
            Icon: EyeSlashIcon,
            callback: () => {
              handleMarkAsRead(false);
            },
          },
        ]),
    ...(isArchived
      ? [
          {
            label: "Unarchive",
            Icon: ArchiveBoxXMarkIcon,
            callback: () => {
              handleUnarchiveChat();
            },
          },
        ]
      : [
          {
            label: "Archive",
            Icon: ArchiveBoxIcon,
            callback: () => {
              handleArchiveChat();
            },
          },
        ]),
  ];

  return (
    <div
      className={clsx(
        "flex flex-row p-2 cursor-pointer items-center rounded-lg",
        isSelected ? "bg-bg-selected" : "bg-white",
      )}
    >
      <div className="flex flex-col w-full gap-1">
        <div className="group flex flex-row gap-2 w-full items-center">
          {chat.isUnread ? (
            <FaCircle className="max-w-3! max-h-3! min-w-3! min-h-3! text-globemallow-500" />
          ) : (
            <div className="pl-3"></div>
          )}
          <Paragraph className="truncate" colorType="primary" displayType="loud">
            {caregiverFirstName} {caregiverLastName}
          </Paragraph>
          {chat.type === ApiChatTypeChoices.Expert && (
            <>
              {chat.needsReview && <Badge text="Needs Review" appearance="warn" />}

              {timeRemainingMinutes > 0 && (
                <div className="flex flex-row gap-1 items-center">
                  <ClockIcon className="w-4 h-4 text-rio-600" />
                  <p className="text-rio-600">{getTimeRemaining(new Date(chat.lastMessage?.createdDate))}</p>
                </div>
              )}
            </>
          )}

          {chat.type == ApiChatTypeChoices.Expert && (
            <div
              className={clsx(
                "ml-auto transition-opacity duration-200 [&_button:hover]:bg-transparent",
                isMobile ? "opacity-100" : "opacity-0 group-hover:opacity-100",
              )}
            >
              <PopoverMenu menuItems={popoverMenuItems} />
            </div>
          )}
        </div>

        <div className="flex flex-row justify-between items-center pl-5 gap-2">
          {chat.type === ApiChatTypeChoices.Expert
            ? chat.lastMessage && (
                <Paragraph className="truncate min-w-0 flex-1">
                  {!chat.lastMessage.content && chat.lastMessage.file ? (
                    "Sent an attachment"
                  ) : (
                    <div className="[&_.ql-editor]:line-clamp-1 [&_.ql-editor]:p-0! [&_.ql-editor]:overflow-hidden!">
                      <WYSIWYG
                        title=""
                        placeholder=""
                        existingContent={chat.lastMessage.content || ""}
                        onSave={async () => {}}
                        isSaving={false}
                        displayOnlyMode={true}
                      />
                    </div>
                  )}
                </Paragraph>
              )
            : learnerFirstName && (
                <Paragraph className="truncate min-w-0 flex-1">
                  Client: {learnerFirstName} {learnerLastName}
                </Paragraph>
              )}
          <Paragraph className="align-end justify-end shrink-0 whitespace-nowrap">
            {formatDate(new Date(chat.lastMessage?.createdDate || chat.lastUpdatedDate))}
          </Paragraph>
        </div>
      </div>
    </div>
  );
};
